import React, { useState , useEffect} from 'react';
import './ApiDetails.css';
import { AdvancedPlans } from '../pricing-plans/AdvancedPlans';
import { getInstanceIdVersionMap } from '../../utils/spec-utils';
import { APIDetailsHeader } from '../common/ApiDetailsTabHeader';
import { PLAN_TYPE, NO_PRICING_PLAN_TEXT, getAllEndpoints, getPlansToShowForAPI } from '../../utils/api-pricing';
import { API_DIRECTORY_THEME } from '../../utils/constants';
import { DAABApiPricing } from './DAABApiPricing';
import { displayLegacyPlans } from '../pricing-plans/LegacyPlans';
import { BTN_TEXT, RESTRICTED_SHARED_STATUS } from '../api-directory-home/constants';
import { Toast, TOAST_POSITIONS, TOAST_VARIANTS } from "cdk-radial";

const isAdvancedPlan = plan =>
  Boolean(
    plan?.type?.toLowerCase() === PLAN_TYPE.ADVANCED &&
    plan?.status?.toLowerCase() === 'active'
  );

  const isSubscriptionPlan = plan =>
  Boolean(
    plan?.type?.toLowerCase() === PLAN_TYPE.SUBSCRIPTION &&
    plan?.status?.toLowerCase() === 'active'
  );

const noPricingText = () => {
  return (
    <div className="api-dir-pkg_no-documentation-text">
      {NO_PRICING_PLAN_TEXT}
    </div>
  );
};
const restrictedApiText = (
  restrictedSharedStatus,
  apiDetailsOnClickHandlers,
  apiId,
  apiType,
  isIsvApiDirectory,
  setIsRequesting,
  setIsRequested,
  setShowSuccessToast
) => {
  const restrictedApiNotRequested =
    restrictedSharedStatus === RESTRICTED_SHARED_STATUS.NOT_REQUESTED;
  return (
    <div className="api-dir-pkg_restricted-api-text">
      {restrictedApiNotRequested ? (
        <span
          role="button"
          tabIndex={0}
          style={{
            cursor: "pointer",
            color: isIsvApiDirectory ? "#5E2368" : "#0066cc",
            textDecoration: "none",
          }}
          onClick={(event) => {
            if (isIsvApiDirectory) {
              apiDetailsOnClickHandlers.handleRequestAccess({
                setIsRequesting,
                setIsRequested,
                setShowSuccessToast,
              });
              event.stopPropagation();
            } else {
    apiDetailsOnClickHandlers.requestRestrictedAccess({
              apiId,
              apiType,
                btn: BTN_TEXT.REQUEST_ACCESS,
            });
              event.stopPropagation();
            }
          }}
        >
          {BTN_TEXT.REQUEST_ACCESS}
        </span>
      ) : (
        <span className="api-dir-pkg_restricted-requested">
          {BTN_TEXT.REQUESTED_ACCESS}
        </span>
      )}{" "}
      for more details.
    </div>
  );
};

const ApiPricing = (props) => {
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [isRequested, setIsRequested] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);

  const removeToast = () => {
    setShowSuccessToast(false);
  };

  useEffect(() => {
    if (
      props.apiInfo.data?.restrictedSharedStatus ===
        RESTRICTED_SHARED_STATUS.REQUESTED &&
      !isRequested
    ) {
      setIsRequested(true);
    }
  }, [props.apiInfo.data?.restrictedSharedStatus, isRequested]);

  const belongsTo = props?.belongsTo;
  const apiPlans = props.apiInfo?.data?.plans?.filter(plan => plan.status === 'active') || [];
  const apiSpec = props.apiInfo?.data?.environments?.[0]?.instances?.[0]?.spec;
  const isAsync = props && props.apiType === 'async-api';
  let plansToShow = belongsTo === API_DIRECTORY_THEME.ISV_API_DIRECTORY ? apiPlans : getPlansToShowForAPI(apiPlans);

  const shouldSkipRestriction =
    props.isContracted || props.ownOrgApi || props.skipApiPricing;

  const isEligibleForRestriction =
    props.apiInfo.data?.isRestricted &&
    !shouldSkipRestriction &&
    [
      RESTRICTED_SHARED_STATUS.NOT_REQUESTED,
      RESTRICTED_SHARED_STATUS.REQUESTED,
    ].includes(props.apiInfo.data?.restrictedSharedStatus);
    
  const prodInstance =
    (Array.isArray(props?.apiInfo?.data?.instances) &&
      props.apiInfo.data.instances.some(
        (f) =>
          f?.environment?.toLowerCase() === "prod" ||
          f?.environment?.toLowerCase() === "production"
      )) ||
    (Array.isArray(props?.apiInfo?.data?.environments) &&
      props.apiInfo.data.environments.some(
        (env) =>
          Array.isArray(env?.instances) &&
          env.instances.some(
            (instance) =>
              instance?.environment?.toLowerCase() === "prod" ||
              instance?.environment?.toLowerCase() === "production"
          )
      )) ||
    (Array.isArray(props?.apiInfo?.data?.environments) &&
      props.apiInfo.data.environments.some(
        (env) =>
          env.name.toLowerCase() === "prod" ||
          env.name.toLowerCase() === "production"
      ));

const displayRatePlans = () => {
  const advancedSubscriptionPlans = plansToShow.filter(p => isAdvancedPlan(p) || isSubscriptionPlan(p) );
  const legacyPlans = plansToShow.filter(p => !isAdvancedPlan(p) && !isSubscriptionPlan(p));

  return (
    <>
      {advancedSubscriptionPlans.length > 0 && (
        <AdvancedPlans
          instanceIdVersionMap={getInstanceIdVersionMap(props.apiInfo)}
          plans={advancedSubscriptionPlans}
          isAsync={isAsync}
          allEndpoints={getAllEndpoints(apiSpec, isAsync)}
          belongsTo={props.belongsTo}
          prodInstance={prodInstance}
          onCompareNowClick={props.onCompareNowClick}
          isAdvancedPlan={props.isAdvancedPlan}
          isCurrentApiInBundleCart={props.isCurrentApiInBundleCart}
        />
      )}
      {legacyPlans.length > 0 && (
        <>
          {legacyPlans.map(ratePlan => displayLegacyPlans(ratePlan.type, ratePlan))}
        </>
      )}
    </>
  );
};

  const displayDealerAPIPricing = () => {
    return (
      <>
        <DAABApiPricing
          instanceIdVersionMap={getInstanceIdVersionMap(props.apiInfo)}
          plans={plansToShow}
          isAsync={isAsync}
          allEndpoints={getAllEndpoints(apiSpec, isAsync)}
          addingPricingPlanToApiInCart={props?.addingPricingPlanToApiInCart}
          addingAPIToCart={props?.addingAPIToCart}
          isMsaConsented={props?.isMsaConsented}
           prodInstance={prodInstance}
           onCompareNowClick={props.onCompareNowClick}
           isCurrentApiInBundleCart ={props.isCurrentApiInBundleCart}
        />
      </>
    );
  };

  return (
    <>
      <APIDetailsHeader title="Pricing" />
      {isEligibleForRestriction
        ? restrictedApiText(
            isRequested
              ? RESTRICTED_SHARED_STATUS.REQUESTED
              : props.apiInfo.data?.restrictedSharedStatus,
            props.apiDetailsOnClickHandlers,
            props.apiId,
            props.apiType,
            belongsTo === API_DIRECTORY_THEME.ISV_API_DIRECTORY,
            setIsRequesting,
            setIsRequested,
            setShowSuccessToast
          )
        : apiPlans.length && !props.bundleDetailsViewEnabled
        ? belongsTo === API_DIRECTORY_THEME.ISV_API_DIRECTORY
          ? displayRatePlans()
          : displayDealerAPIPricing()
        : noPricingText()}

      {showSuccessToast && belongsTo === API_DIRECTORY_THEME.ISV_API_DIRECTORY && (
        <Toast
          actions={[
            {
              onClick: removeToast,
              text: "Dismiss",
            },
          ]}
          position={TOAST_POSITIONS.FIXED}
          variant={TOAST_VARIANTS.POSITIVE}
          onVisibilityDurationEnd={removeToast}
          id="api-pricing-request-toast"
          content="Your request has been submitted and you will be updated via email."
          style={{
            zIndex: 1000,
            Height: "88px",
            minWidth: "497px",
            marginLeft: "929px",
          }}
        />
      )}
    </>
  );
};

export default ApiPricing;
